// @spec GHG-STR-005
// @spec GHG-STR-086
/**
 * TanStack Query hooks for GHG grid regions.
 */

import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';

import type { GridRegion } from '../types';

import { fetchGridRegions } from './grid-regions';
import { useGHGOrganizationId } from './use-ghg-organization-id';

import { coerceNumeric } from '@/lib/format';

export const gridRegionKeys = {
  all: ['ghg-grid-regions'] as const,
  list: (orgId: string, country?: string, query?: string) =>
    [...gridRegionKeys.all, orgId, country ?? '', query ?? ''] as const,
};

/**
 * Normalize a grid region item from the API to ensure co2ePerKwh is a
 * proper JS number (or null). Handles Decimal-as-string edge cases.
 */
function normalizeGridRegion(raw: GridRegion): GridRegion {
  return {
    ...raw,
    co2ePerKwh: coerceNumeric(raw.co2ePerKwh),
  };
}

/**
 * Hook to fetch available grid regions.
 */
export function useGridRegions(country?: string, query?: string) {
  const { getToken, isSignedIn } = useAuth();
  const organizationId = useGHGOrganizationId();

  const queryResult = useQuery({
    queryKey: gridRegionKeys.list(organizationId ?? '', country, query),
    queryFn: async () => {
      if (!organizationId) {
        throw new Error('Organization not found');
      }
      return fetchGridRegions(getToken, organizationId, country, query);
    },
    enabled: (isSignedIn ?? false) && !!organizationId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const normalizedRegions = (queryResult.data?.items ?? []).map(normalizeGridRegion);

  return {
    gridRegions: normalizedRegions,
    lastSyncedAt: queryResult.data?.lastSyncedAt,
    isStale: queryResult.data?.isStale ?? false,
    isLoading: queryResult.isLoading,
    isError: queryResult.isError,
    error: queryResult.error,
    refetch: queryResult.refetch,
  };
}
